import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, model } from '@angular/core';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { injectSearchAppBar } from '@jtms/default-connector/angular';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Router, RouterLink } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccessControlService } from '@dis/auth';
import { customerIconUrlFromCustomerName } from '@dis/models';
import { BarcodeScannerComponent, PartImageComponent } from '@dis/shared';
import { ENTITY_ROUTES } from "projects/jtms-front-end/src/app/core/routing/entity-routes";

// Hosts whose links (e.g. shipment label QR codes) are routed in-app instead of searched.
const JTMS_ORIGINS = ['https://jtms.drillingllc.com', 'https://jtms-dev.web.app'];

@Component({
  selector: 'jtms-search-bar-results',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatListModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    RouterLink,
    DatePipe,
    PartImageComponent,
  ],
  templateUrl: './search-bar-results.html',
  styleUrl: './search-bar-results.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchBarResults {
  protected readonly entityRoutes = ENTITY_ROUTES;

  searchString = model<string>('');
  #access = inject(AccessControlService);
  $searchResults = injectSearchAppBar(() => ({ searchString: this.searchString(), ...this.#access.viewScope() }));

  #dialogRef = inject(MatDialogRef<SearchBarResults>);
  #dialog = inject(MatDialog);
  #router = inject(Router);

  getCustomerLogoUrl(customerShortName: string): string {
    return customerIconUrlFromCustomerName(customerShortName);
  }

  closeModal(): void {
    this.#dialogRef.close();
  }

  openScannerDialog(): void {
    this.#dialog
      .open<BarcodeScannerComponent, unknown, string | false>(BarcodeScannerComponent)
      .afterClosed()
      .subscribe((result) => {
        if (!result) return;
        const path = jtmsPathFromScan(result);
        if (path) {
          void this.#router.navigateByUrl(path);
          this.closeModal();
        } else {
          this.searchString.set(result);
        }
      });
  }
}

/** The in-app path for a scanned JTMS link, or null when the scan isn't one (e.g. a serial number barcode). */
function jtmsPathFromScan(scan: string): string | null {
  let url: URL;
  try {
    url = new URL(scan.trim());
  } catch {
    return null;
  }
  const isJtms = url.origin === window.location.origin || JTMS_ORIGINS.includes(url.origin);
  return isJtms ? `${url.pathname}${url.search}${url.hash}` : null;
}
